// /app/frontend/src/components/admin/fabric/BulkMatchDialog.jsx
// Dialog to preview & apply auto-matched Publitas textures for a collection

import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, Wand2, SlidersHorizontal } from 'lucide-react';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BulkMatchDialog = ({ open, onOpenChange, collectionId, collectionName, token, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rawMatches, setRawMatches] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [threshold, setThreshold] = useState([50]); // 0-100 slider
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && collectionId) {
      fetchPreview();
    }
  }, [open, collectionId]);

  const fetchPreview = async () => {
    setLoading(true);
    setRawMatches(null);
    try {
      // Fetch with low threshold — we'll filter client-side with the slider
      const { data } = await axios.post(`${API}/api/admin/bulk-texture-match/preview`, {
        collection_id: collectionId,
        min_score: 0.0,
      }, { headers });
      setRawMatches(data);
      setSelected(new Set(data.matches.filter(m => m.score >= 0.5).map(m => m.color_id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan matches');
    } finally {
      setLoading(false);
    }
  };

  // Filter matches by threshold slider
  const filteredMatches = useMemo(() => {
    if (!rawMatches) return [];
    const minScore = threshold[0] / 100;
    return rawMatches.matches.filter(m => m.score >= minScore);
  }, [rawMatches, threshold]);

  const unmatched = rawMatches ? rawMatches.total_colors - filteredMatches.length : 0;

  const toggleMatch = (colorId) => {
    const next = new Set(selected);
    next.has(colorId) ? next.delete(colorId) : next.add(colorId);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filteredMatches.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredMatches.map(m => m.color_id)));
    }
  };

  const handleApply = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    try {
      const matchList = filteredMatches
        .filter(m => selected.has(m.color_id))
        .map(m => ({ color_id: m.color_id, publitas_color_id: m.matched_texture_id }));

      const { data } = await axios.post(`${API}/api/admin/bulk-texture-match/apply`, {
        collection_id: collectionId,
        matches: matchList,
      }, { headers });

      toast.success(`Applied ${data.applied} texture replacements`);
      onApplied?.(data.applied);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply matches');
    } finally {
      setApplying(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 0.9) return 'text-emerald-700 bg-emerald-100';
    if (score >= 0.7) return 'text-amber-700 bg-amber-100';
    return 'text-neutral-600 bg-neutral-100';
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${API}${url}` : url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="bulk-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-amber-600" />
            Bulk Texture Match — {collectionName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            <p className="text-sm text-neutral-500">Analyzing color names and finding matches...</p>
          </div>
        ) : rawMatches ? (
          <>
            {/* Threshold Slider */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-neutral-50 border border-neutral-200" data-testid="threshold-control">
              <SlidersHorizontal className="w-4 h-4 text-neutral-500 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-neutral-500">Min Confidence</span>
                  <span className="text-xs font-bold text-amber-700">{threshold[0]}%</span>
                </div>
                <Slider
                  value={threshold}
                  onValueChange={setThreshold}
                  min={0}
                  max={100}
                  step={5}
                  data-testid="threshold-slider"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium">{rawMatches.total_colors} colors</span>
              <span className="text-emerald-700">{filteredMatches.length} matched</span>
              <span className="text-neutral-500">{unmatched} below threshold</span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={toggleAll} data-testid="select-all-matches">
                {selected.size === filteredMatches.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {/* Match list */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-1.5">
              {filteredMatches.map(m => (
                <div
                  key={m.color_id}
                  onClick={() => toggleMatch(m.color_id)}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                    selected.has(m.color_id) ? 'bg-amber-50 border-amber-200' : 'bg-white border-neutral-100 hover:border-neutral-300'
                  } ${m.already_replaced ? 'opacity-60' : ''}`}
                  data-testid={`bulk-match-row-${m.color_id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(m.color_id)}
                    onChange={() => toggleMatch(m.color_id)}
                    className="w-4 h-4 rounded border-neutral-300 text-amber-600 flex-shrink-0"
                    onClick={e => e.stopPropagation()}
                  />

                  {/* Current color */}
                  <div className="w-10 h-10 rounded-lg border border-neutral-200 overflow-hidden bg-neutral-100 flex-shrink-0">
                    {m.color_image_url ? (
                      <img src={imgSrc(m.color_image_url)}
                        alt={m.color_name} className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full bg-neutral-200" />
                    )}
                  </div>
                  <div className="min-w-0 w-28 flex-shrink-0">
                    <p className="text-xs font-medium text-neutral-800 truncate">{m.color_name}</p>
                    {m.already_replaced && <p className="text-[9px] text-amber-600">Already replaced</p>}
                  </div>

                  <span className="text-neutral-300 text-sm">→</span>

                  {/* Matched texture */}
                  <div className="w-10 h-10 rounded-lg border-2 border-amber-300 overflow-hidden bg-neutral-100 flex-shrink-0">
                    {m.matched_texture_image && (
                      <img src={imgSrc(m.matched_texture_image)}
                        alt={m.matched_texture_name} className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = 'none'; }} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-neutral-800 truncate">{m.matched_texture_name}</p>
                  </div>

                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${scoreColor(m.score)}`}>
                    {Math.round(m.score * 100)}%
                  </span>
                </div>
              ))}

              {filteredMatches.length === 0 && (
                <div className="text-center py-12 text-neutral-500 text-sm">
                  {rawMatches.matches.length > 0
                    ? 'No matches above the current threshold. Try lowering it.'
                    : 'No matching textures found. Sync a Publitas catalog first.'}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-neutral-500">Something went wrong.</div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleApply}
            disabled={selected.size === 0 || applying}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="apply-bulk-match-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Apply {selected.size} Matches
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkMatchDialog;
